import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  message,
  Spin,
  Statistic,
  Row,
  Col,
  Typography,
  Divider,
  List,
  Badge,
} from 'antd';
import {
  UserOutlined,
  SyncOutlined,
  HistoryOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { getCurrentUser } from '../../services/auth';
import { syncWeChatUsers, getSyncStatus, getSyncLogs, SyncLog } from '../../services/wechat';

const { Title, Text } = Typography;

interface User {
  id: number;
  username: string;
  email: string;
  real_name?: string;
  phone?: string;
  department?: string;
  role: string;
  status: string;
  wechat_userid?: string;
  created_at: string;
  updated_at?: string;
  role_override?: boolean;
  max_leads?: number;
}

const Users: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [editRoleVisible, setEditRoleVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editRoleForm, setEditRoleForm] = useState<{ role: string; status: string; role_override: boolean; max_leads: number }>({ role: '', status: '', role_override: false, max_leads: 300 });

  useEffect(() => {
    loadCurrentUser();
    loadUsers();
    loadSyncStatus();
  }, []);

  const loadCurrentUser = async () => {
    try {
      const user = await getCurrentUser();
      setCurrentUser(user);
    } catch (error) {
      console.error('获取当前用户失败:', error);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const { getUsers } = await import('../../services/user');
      const response = await getUsers({ skip: 0, limit: 100 });
      // 后端直接返回数组，不是 { items: [] } 格式
      const userList = Array.isArray(response) ? response : (response.items || []);
      setUsers(userList);
    } catch (error) {
      console.error('加载用户列表失败:', error);
      message.error('加载用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const loadSyncStatus = async () => {
    try {
      const data = await getSyncStatus();
      setSyncStatus(data);
    } catch (error) {
      console.error('获取同步状态失败:', error);
    }
  };

  const handleSync = async () => {
    Modal.confirm({
      title: '确认同步',
      content: '确定要从企业微信同步通讯录吗？这可能需要几分钟时间。',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        setSyncing(true);
        try {
          const result = await syncWeChatUsers();
          
          if (result.success) {
            message.success(
              `同步完成！新建 ${result.user_stats.created} 个用户，更新 ${result.user_stats.updated} 个用户`
            );
            loadUsers();
            loadSyncStatus();
          } else {
            message.error('同步失败，请查看详细日志');
          }
        } catch (error) {
          message.error('同步失败，请稍后重试');
        } finally {
          setSyncing(false);
        }
      },
    });
  };

  const loadSyncLogs = async () => {
    try {
      const data = await getSyncLogs(0, 10);
      setSyncLogs(data.items || []);
      setHistoryVisible(true);
    } catch (error) {
      message.error('获取同步历史失败');
    }
  };

  const getSyncStatusBadge = (status: string) => {
    const statusMap: Record<string, { status: any; text: string }> = {
      success: { status: 'success', text: '成功' },
      failed: { status: 'error', text: '失败' },
      partial: { status: 'warning', text: '部分成功' },
      running: { status: 'processing', text: '进行中' },
    };
    return statusMap[status] || { status: 'default', text: status };
  };

  const handleEditRole = (user: User) => {
    setEditingUser(user);
    setEditRoleForm({
      role: user.role,
      status: user.status,
      role_override: user.role_override || false,
      max_leads: user.max_leads || 300,
    });
    setEditRoleVisible(true);
  };

  const handleSaveRole = async () => {
    if (!editingUser) return;

    try {
      const { updateUser } = await import('../../services/user');
      // 手动修改角色后，自动设置 role_override = true
      await updateUser(editingUser.id, {
        role: editRoleForm.role as any,
        status: editRoleForm.status as any,
        role_override: true, // 手动修改后自动锁定
        max_leads: editRoleForm.max_leads,
      });
      message.success('用户信息更新成功（角色已锁定，不会被同步覆盖）');
      setEditRoleVisible(false);
      loadUsers();
    } catch (error) {
      console.error('更新用户信息失败:', error);
      message.error('更新用户信息失败');
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: '用户信息',
      key: 'user_info',
      width: 280,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            <Text strong>{record.real_name || record.username}</Text>
            {record.wechat_userid && (
              <Tag icon={<CheckCircleOutlined />} color="blue" style={{ margin: 0 }}>
                企业微信
              </Tag>
            )}
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.username}
          </Text>
        </Space>
      ),
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
    },
    {
      title: '部门',
      dataIndex: 'department',
      key: 'department',
      width: 120,
      render: (department) => department || <Text type="secondary">-</Text>,
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 150,
      align: 'center',
      render: (role, record) => {
        const roleMap: Record<string, { color: string; text: string }> = {
          admin: { color: 'red', text: '管理员' },
          finance: { color: 'orange', text: '财务' },
          staff: { color: 'blue', text: '员工' },
        };
        const roleInfo = roleMap[role] || { color: 'default', text: role };
        return (
          <Space>
            <Tag color={roleInfo.color}>{roleInfo.text}</Tag>
            {record.role_override ? (
              <Tag icon={<CheckCircleOutlined />} color="blue" title="角色已锁定，不会被同步覆盖">
                🔒
              </Tag>
            ) : (
              <Tag color="default" title="角色自动判断，同步时可能更新">
                🔓
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      align: 'center',
      render: (status) => (
        <Tag color={status === 'active' ? 'success' : 'error'}>
          {status === 'active' ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      align: 'center',
      render: (_, record) => (
        isAdmin ? (
          <Button
            type="link"
            size="small"
            onClick={() => handleEditRole(record)}
          >
            编辑
          </Button>
        ) : null
      ),
    },
  ];

  // 只有管理员才能看到同步功能
  const isAdmin = currentUser?.role === 'admin';

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3}>用户管理</Title>
            <Text type="secondary">管理系统用户和企业微信通讯录同步</Text>
          </div>
        </div>

        {isAdmin && (
          <Card title="企业微信通讯录同步" extra={
            <Space>
              <Button
                icon={<HistoryOutlined />}
                onClick={loadSyncLogs}
              >
                同步历史
              </Button>
              <Button
                type="primary"
                icon={<SyncOutlined spin={syncing} />}
                onClick={handleSync}
                loading={syncing}
              >
                同步通讯录
              </Button>
            </Space>
          }>
            {syncStatus?.has_sync ? (
              <>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="最近同步状态"
                      value={getSyncStatusBadge(syncStatus.sync_status).text}
                      prefix={
                        syncStatus.sync_status === 'success' ? (
                          <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        ) : syncStatus.sync_status === 'failed' ? (
                          <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                        ) : (
                          <ClockCircleOutlined style={{ color: '#faad14' }} />
                        )
                      }
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic 
                      title="同步总数" 
                      value={syncStatus.total_count}
                      suffix="个"
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="成功"
                      value={syncStatus.success_count}
                      suffix="个"
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="失败"
                      value={syncStatus.error_count}
                      suffix="个"
                      valueStyle={{ color: syncStatus.error_count > 0 ? '#ff4d4f' : undefined }}
                    />
                  </Col>
                </Row>
                {syncStatus?.started_at && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                    <Text type="secondary">
                      最近同步时间: {dayjs(syncStatus.started_at).format('YYYY-MM-DD HH:mm:ss')}
                    </Text>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <Text type="secondary">尚未执行过同步，点击右上角"同步通讯录"按钮开始同步</Text>
              </div>
            )}
          </Card>
        )}

        <Card 
          title={
            <Space>
              <UserOutlined />
              <span>用户列表</span>
              <Tag color="blue">{users.length} 人</Tag>
            </Space>
          }
        >
          <Table
            columns={columns}
            dataSource={users}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 个用户`,
            }}
            size="middle"
          />
        </Card>
      </Space>

      <Modal
        title="同步历史记录"
        open={historyVisible}
        onCancel={() => setHistoryVisible(false)}
        footer={null}
        width={800}
      >
        <List
          dataSource={syncLogs}
          renderItem={(log) => {
            const statusBadge = getSyncStatusBadge(log.sync_status);
            return (
              <List.Item>
                <List.Item.Meta
                  avatar={
                    <Badge status={statusBadge.status} />
                  }
                  title={
                    <Space>
                      <Text strong>{statusBadge.text}</Text>
                      <Text type="secondary">
                        {dayjs(log.started_at).format('YYYY-MM-DD HH:mm:ss')}
                      </Text>
                    </Space>
                  }
                  description={
                    <Space split={<Divider type="vertical" />}>
                      <Text>总数: {log.total_count}</Text>
                      <Text type="success">新建: {log.created_count}</Text>
                      <Text>更新: {log.updated_count}</Text>
                      {log.error_count > 0 && (
                        <Text type="danger">错误: {log.error_count}</Text>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            );
          }}
        />
      </Modal>

      <Modal
        title="编辑用户信息"
        open={editRoleVisible}
        onCancel={() => setEditRoleVisible(false)}
        onOk={handleSaveRole}
        okText="保存"
        cancelText="取消"
        width={600}
      >
        {editingUser && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div>
              <Text strong>用户：</Text>
              <Text>{editingUser.real_name || editingUser.username} ({editingUser.username})</Text>
            </div>
            
            <div>
              <Space>
                <div>
                  <Text strong>当前角色：</Text>
                  <Tag color={
                    editingUser.role === 'admin' ? 'red' :
                    editingUser.role === 'finance' ? 'orange' : 'blue'
                  }>
                    {editingUser.role === 'admin' ? '管理员' :
                     editingUser.role === 'finance' ? '财务' : '员工'}
                  </Tag>
                  {editingUser.role_override ? (
                    <Tag color="blue">🔒 已锁定</Tag>
                  ) : (
                    <Tag color="default">🔓 自动判断</Tag>
                  )}
                </div>
                <div>
                  <Text strong>当前状态：</Text>
                  <Tag color={editingUser.status === 'active' ? 'success' : 'error'}>
                    {editingUser.status === 'active' ? '启用' : '禁用'}
                  </Tag>
                </div>
              </Space>
            </div>

            <Divider style={{ margin: '8px 0' }} />

            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>登录权限：</Text>
              <Space direction="vertical">
                <label style={{ cursor: 'pointer' }}>
                  <input
                    type="radio"
                    value="active"
                    checked={editRoleForm.status === 'active'}
                    onChange={(e) => setEditRoleForm({ ...editRoleForm, status: e.target.value })}
                  />
                  <span style={{ marginLeft: 8 }}>
                    <Tag color="success" style={{ margin: '0 4px' }}>启用</Tag>
                    允许登录系统
                  </span>
                </label>
                <label style={{ cursor: 'pointer' }}>
                  <input
                    type="radio"
                    value="disabled"
                    checked={editRoleForm.status === 'disabled'}
                    onChange={(e) => setEditRoleForm({ ...editRoleForm, status: e.target.value })}
                  />
                  <span style={{ marginLeft: 8 }}>
                    <Tag color="error" style={{ margin: '0 4px' }}>禁用</Tag>
                    禁止登录系统
                  </span>
                </label>
              </Space>
            </div>

            <Divider style={{ margin: '8px 0' }} />

            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>用户角色：</Text>
              <Space direction="vertical">
                <label style={{ cursor: 'pointer' }}>
                  <input
                    type="radio"
                    value="admin"
                    checked={editRoleForm.role === 'admin'}
                    onChange={(e) => setEditRoleForm({ ...editRoleForm, role: e.target.value })}
                  />
                  <span style={{ marginLeft: 8 }}>
                    <Tag color="red" style={{ margin: '0 4px' }}>管理员</Tag>
                    拥有所有权限
                  </span>
                </label>
                <label style={{ cursor: 'pointer' }}>
                  <input
                    type="radio"
                    value="finance"
                    checked={editRoleForm.role === 'finance'}
                    onChange={(e) => setEditRoleForm({ ...editRoleForm, role: e.target.value })}
                  />
                  <span style={{ marginLeft: 8 }}>
                    <Tag color="orange" style={{ margin: '0 4px' }}>财务</Tag>
                    财务管理权限
                  </span>
                </label>
                <label style={{ cursor: 'pointer' }}>
                  <input
                    type="radio"
                    value="staff"
                    checked={editRoleForm.role === 'staff'}
                    onChange={(e) => setEditRoleForm({ ...editRoleForm, role: e.target.value })}
                  />
                  <span style={{ marginLeft: 8 }}>
                    <Tag color="blue" style={{ margin: '0 4px' }}>员工</Tag>
                    基础操作权限
                  </span>
                </label>
              </Space>
            </div>

            <Divider style={{ margin: '8px 0' }} />

            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>线索数量限制：</Text>
              <Space>
                <input
                  type="number"
                  min="1"
                  max="10000"
                  value={editRoleForm.max_leads}
                  onChange={(e) => setEditRoleForm({ ...editRoleForm, max_leads: parseInt(e.target.value) || 300 })}
                  style={{
                    width: 120,
                    padding: '4px 11px',
                    fontSize: 14,
                    lineHeight: '1.5715',
                    border: '1px solid #d9d9d9',
                    borderRadius: 2,
                  }}
                />
                <Text type="secondary">条（该用户最多可认领的线索数量）</Text>
              </Space>
            </div>

            <div style={{ 
              marginTop: 16, 
              padding: 12, 
              backgroundColor: '#f0f5ff', 
              borderRadius: 4,
              border: '1px solid #d6e4ff'
            }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                💡 提示：手动修改角色后，该用户的角色将被锁定，企业微信同步不会再修改此用户的角色。
              </Text>
            </div>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default Users;
